import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Quiet primary: full-strength primary TEXT on a 10% tint — for the
        // one must-take action inside subdued chrome (expanded-row panels)
        // where a solid bg-primary fill would overpower the surface. Same
        // tint vocabulary as selected rows and highlighted totals.
        soft: "bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20",
        // Text link with a permanent underline at 30% of the text colour, for
        // the touch surfaces (docs/MOBILE.md: one compact primary per screen,
        // everything else a text link). 44px tap height, no background.
        underlined:
          "self-start bg-transparent text-[15px] font-medium text-foreground underline underline-offset-4 [text-decoration-color:color-mix(in_srgb,currentColor_30%,transparent)] active:opacity-80",
        // The same link on a header-coloured band (photo band, header):
        // header foreground with a 40% underline.
        underlinedOnBand:
          "self-start bg-transparent text-[15px] font-medium text-header-foreground underline underline-offset-4 [text-decoration-color:color-mix(in_srgb,currentColor_40%,transparent)] active:opacity-80",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Touch sizes for the /apps/* phone surfaces (docs/MOBILE.md §4.1).
        // Additive: the desktop sizes above are unchanged.
        touch: "h-12 px-4 py-2 text-base font-medium",
        touchIcon: "h-11 w-11",
        // The one compact primary on a touch screen: 44px, sized to its label,
        // left aligned, never full width (docs/MOBILE.md).
        touchCompact: "h-11 w-auto self-start rounded-lg px-5 text-[15px] font-medium",
      },
    },
    compoundVariants: [
      // Text links carry no box: they keep a 44px tap height and drop the
      // size's height and padding whatever size is passed.
      {
        variant: ["underlined", "underlinedOnBand"],
        className: "h-auto min-h-11 rounded-none p-0",
      },
      { variant: "default", size: "touchCompact", className: "active:bg-primary/80" },
      { variant: "destructive", size: "touchCompact", className: "active:bg-destructive/80" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
